import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, CheckCircle2Icon, LoaderCircleIcon, MailIcon, PhoneIcon, MapPinIcon } from 'lucide-react';
import { profile, inquiryTypes } from '../data/profile';
interface ContactModalProps {
  open: boolean;
  onClose: () => void;
}
type Status = 'idle' | 'submitting' | 'success' | 'error';
const initialForm = {
  name: '',
  organization: '',
  email: '',
  inquiry: inquiryTypes[0],
  message: ''
};
export function ContactModal({
  open,
  onClose
}: ContactModalProps) {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState<Status>('idle');
  const firstFieldRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    const focusTimer = window.setTimeout(() => firstFieldRef.current?.focus(), 80);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
      window.clearTimeout(focusTimer);
    };
  }, [open, onClose]);
  useEffect(() => {
    if (open) return;
    const resetTimer = window.setTimeout(() => {
      setForm(initialForm);
      setStatus('idle');
    }, 250);
    return () => window.clearTimeout(resetTimer);
  }, [open]);
  const update = (key: keyof typeof initialForm, value: string) => setForm(prev => ({
    ...prev,
    [key]: value
  }));
  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 900);
  };
  const fieldClass = 'focus-ring w-full border border-steel-200 bg-white px-4 py-3 text-sm text-navy transition-colors duration-150 ease-exec placeholder:text-steel-300 hover:border-steel-400';
  return <AnimatePresence data-id="element-46" data-mp="pmw4dujys9he">
      {open ? <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8" data-id="element-47" data-mp="pltdvnqk0920">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={onClose} className="fixed inset-0 bg-navy/60" aria-hidden="true" data-id="element-48" data-mp="bm1x5f8nkekb" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="contact-modal-title" initial={{
        opacity: 0,
        y: 16,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 8,
        scale: 0.98
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative my-auto w-full max-w-3xl bg-white shadow-2xl" data-id="element-49" data-mp="6pbf4mdaf8w9">
            <div className="flex items-start justify-between gap-6 border-b border-steel-100 px-8 py-7" data-id="element-50" data-mp="30gld5qlnop5h">
              <div data-id="element-51" data-mp="2vitkgud6sz08">
                <p className="text-[11px] font-semibold uppercase tracking-overline text-bronze-600" data-id="element-52" data-mp="2vjbowea06jj8">
                  Executive Contact
                </p>
                <h2 id="contact-modal-title" className="mt-3 font-serif text-2xl leading-snug text-navy" data-id="element-53" data-mp="35ghh5pucsgl1">
                  Request an Advisory or Board Briefing
                </h2>
              </div>
              <button type="button" onClick={onClose} aria-label="Close contact form" className="focus-ring -mr-2 -mt-2 rounded-sm p-2 text-steel-400 transition-colors duration-150 ease-exec hover:text-navy" data-id="element-54" data-mp="3egctvz3k4jn6">
                <XIcon className="h-5 w-5" data-id="element-55" data-mp="61vu9frlfhrq" />
              </button>
            </div>

            {status === 'success' ? <div className="px-8 py-14 text-center" data-id="element-56" data-mp="1xefxw405woc">
                <CheckCircle2Icon className="mx-auto h-10 w-10 text-bronze-600" aria-hidden="true" data-id="element-57" data-mp="1vh6mls58iqor" />
                <h3 className="mt-5 font-serif text-2xl text-navy" data-id="element-58" data-mp="20eyewn9sketh">
                  Your request has been received
                </h3>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-steel-400" data-id="element-59" data-mp="2a7dwhjh6f1du">
                  Thank you, {form.name.split(' ')[0] || 'and'}. Inquiries are
                  reviewed directly by Thomas Conroy, typically within two
                  business days.
                </p>
                <button type="button" onClick={onClose} className="focus-ring mt-8 rounded-sm bg-navy px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-150 ease-exec hover:bg-navy-700" data-id="element-60" data-mp="1mha282pvodzi">
                  Close
                </button>
              </div> : <form onSubmit={onSubmit} noValidate className="px-8 py-8" data-id="element-61" data-mp="qaxa2bbmdgga">
                <div className="grid gap-5 sm:grid-cols-2" data-id="element-62" data-mp="qa5bqsqn14mb">
                  <label className="block" data-id="element-63" data-mp="m93euik714ni">
                    <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-navy" data-id="element-64" data-mp="mbg2nccmu98h">
                      Full name
                    </span>
                    <input ref={firstFieldRef} type="text" value={form.name} onChange={e => update('name', e.target.value)} className={fieldClass} placeholder="Jane Whitfield" required data-id="element-65" data-mp="ciqtio4k17gb" />
                  </label>
                  <label className="block" data-id="element-66" data-mp="3pr3os39b7k3r">
                    <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-navy" data-id="element-67" data-mp="3ps52yglmgmpp">
                      Organization
                    </span>
                    <input type="text" value={form.organization} onChange={e => update('organization', e.target.value)} className={fieldClass} placeholder="Board, owner, or firm" data-id="element-68" data-mp="1o00bf681brfi" />
                  </label>
                  <label className="block" data-id="element-69" data-mp="1y0swyy88wi3j">
                    <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-navy" data-id="element-70" data-mp="232s1wcjmvn2l">
                      Email
                    </span>
                    <input type="email" value={form.email} onChange={e => update('email', e.target.value)} className={fieldClass} placeholder="name@company.com" required data-id="element-71" data-mp="2uswfnscw1buu" />
                  </label>
                  <label className="block" data-id="element-72" data-mp="15d6vpwmhppju">
                    <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-navy" data-id="element-73" data-mp="1ad1jhrayxz0o">
                      Nature of inquiry
                    </span>
                    <select value={form.inquiry} onChange={e => update('inquiry', e.target.value)} className={fieldClass} data-id="element-74" data-mp="12rpduwapvayg">
                      {inquiryTypes.map(type => <option key={type} value={type} data-id="element-75" data-mp="16w6uimls5850">
                          {type}
                        </option>)}
                    </select>
                  </label>
                </div>

                <label className="mt-5 block" data-id="element-76" data-mp="f5irf50jpsop">
                  <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-navy" data-id="element-77" data-mp="sv2u6au4mpy0">
                    Brief
                  </span>
                  <textarea value={form.message} onChange={e => update('message', e.target.value)} rows={4} className={fieldClass} placeholder="Program scope, governance question, or board mandate." required data-id="element-78" data-mp="o3bbsnl4jqiy" />
                </label>

                {status === 'error' ? <p role="alert" className="mt-4 text-sm font-medium text-red-700" data-id="element-79" data-mp="3b2xtl4r9vcis">
                    Please provide your name, email, and a brief description of
                    your inquiry.
                  </p> : null}

                <div className="mt-7 flex flex-col-reverse gap-4 border-t border-steel-100 pt-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-80" data-mp="3pz6nrrwb2zoc">
                  <ul className="space-y-1.5 text-xs text-steel-400" data-id="element-81" data-mp="3txw0di9isd6x">
                    <li className="flex items-center gap-2" data-id="element-82" data-mp="2mmbddrieumf">
                      <MailIcon className="h-3.5 w-3.5 text-bronze-600" aria-hidden="true" data-id="element-83" data-mp="7kxhpeilimzw" />
                      {profile.contact.email}
                    </li>
                    <li className="flex items-center gap-2" data-id="element-84" data-mp="che7ed5g19f3">
                      <PhoneIcon className="h-3.5 w-3.5 text-bronze-600" aria-hidden="true" data-id="element-85" data-mp="hjmyt52o43f3" />
                      {profile.contact.phoneDisplay}
                    </li>
                    <li className="flex items-center gap-2" data-id="element-86" data-mp="1x103ow69xjkk">
                      <MapPinIcon className="h-3.5 w-3.5 text-bronze-600" aria-hidden="true" data-id="element-87" data-mp="1x3ma2shfht17" />
                      {profile.contact.address}
                    </li>
                  </ul>
                  <button type="submit" disabled={status === 'submitting'} className="focus-ring inline-flex items-center justify-center gap-2 rounded-sm bg-navy px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-150 ease-exec hover:bg-navy-700 disabled:cursor-not-allowed disabled:opacity-70" data-id="element-88" data-mp="1n56ikoehohr6">
                    {status === 'submitting' ? <>
                        <LoaderCircleIcon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-89" data-mp="33in5mx2jk979" />
                        Sending
                      </> : 'Submit Request'}
                  </button>
                </div>
              </form>}
          </motion.div>
        </div> : null}
    </AnimatePresence>;
}